import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MaterialsService } from '../materials.service';
import { Button } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { Drawer } from 'primeng/drawer';
import { AsyncPipe, CurrencyPipe, DecimalPipe } from '@angular/common';
import {
  Material,
  MaterialAttachment,
  MIN_MATERIAL_SHEET_DIMENSION_MM,
} from '../../models/material.model';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { isPermissionError, parseApiError } from '../quotes-api-error';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { ChipModule } from 'primeng/chip';

import { CheckboxModule } from 'primeng/checkbox';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { FloatLabelModule } from 'primeng/floatlabel';
import { Divider } from 'primeng/divider';
import { Tooltip } from 'primeng/tooltip';
import { combineLatest, map, startWith, Subscription } from 'rxjs';
import { SharedConfirmDialogComponent } from '@tangible/shared';
import { ConfirmationService } from 'primeng/api';
import { SettingsService } from '../settings-page/settings-service/settings.service';
import { UserService } from '../services/user.service';
import { User } from '../../models/user.model';
import { FileUploadModule, FileSelectEvent } from 'primeng/fileupload';
import { DownloadService } from '../services/download.service';
import { Dialog } from 'primeng/dialog';
import { Message } from 'primeng/message';
import { DFM_CHECK_DEFINITIONS } from '../../models/dfm-check-definitions';

@Component({
  selector: 'app-materials-page',
  imports: [
    Button,
    TableModule,
    Drawer,
    AsyncPipe,
    CurrencyPipe,
    DecimalPipe,
    ReactiveFormsModule,
    InputTextModule,
    InputNumberModule,

    CheckboxModule,
    ToggleSwitchModule,
    TextareaModule,
    SelectModule,
    FloatLabelModule,
    Divider,
    Tooltip,
    SharedConfirmDialogComponent,
    ChipModule,
    FileUploadModule,
    Dialog,
    Message,
  ],
  providers: [ConfirmationService],
  templateUrl: './materials-page.component.html',
  styleUrl: './materials-page.component.scss',
})
export class MaterialsPageComponent implements OnInit, OnDestroy {
  materialsService = inject(MaterialsService);
  settingsService = inject(SettingsService);
  userService = inject(UserService);
  confirmationService = inject(ConfirmationService);
  downloadService = inject(DownloadService);
  isDrawerVisible = false;
  materialInDraw: Material | null = null;
  currentUser: User | undefined;
  includeArchived = new FormControl<boolean>(false);

  private subscriptions = new Subscription();

  // Deferred upload state
  pendingFiles: File[] = [];
  deletedAttachmentIds: number[] = [];

  saveError: string | null = null;

  // Attachments modal
  isAttachmentsModalVisible = false;
  modalMaterial: Material | null = null;

  readonly MAX_FILE_SIZE = 50 * 1000 * 1000; // 50 MB in bytes
  readonly minSheetDimensionMm = MIN_MATERIAL_SHEET_DIMENSION_MM;
  readonly dfmDefinitions = DFM_CHECK_DEFINITIONS;

  currencyCode$ = this.settingsService.settings$.pipe(
    map((settings) => settings?.organisation_base_currency_code || 'GBP'),
  );

  tableMaterials$ = combineLatest([
    this.includeArchived.valueChanges.pipe(
      startWith(this.includeArchived.value),
    ),
    this.materialsService.activeMaterials$,
    this.materialsService.materials$,
  ]).pipe(
    map(([includeArchived, activeMaterials, allMaterials]) =>
      includeArchived ? allMaterials : activeMaterials,
    ),
  );

  materialFormGroup = new FormGroup({
    name: new FormControl('', [Validators.required]),
    price: new FormControl('0'),
    priceType: new FormControl<'area' | 'volume' | 'volume_l'>('area'),
    sheetSizes: new FormArray<
      FormGroup<{
        x: FormControl<number | null>;
        y: FormControl<number | null>;
        z: FormControl<number | null>;
      }>
    >([]),
    densityGramsPerm3: new FormControl<number | null>(0, [
      Validators.required,
      Validators.min(0),
    ]),
    surfaceHardness: new FormControl<number | null>(null, [
      Validators.min(1),
      Validators.max(10000),
    ]),
    curedThicknessMm: new FormControl<number | null>(null, [
      Validators.min(0.001),
    ]),
    compositeTimeMultiplier: new FormControl<number | null>(1.0, [
      Validators.min(0.1),
      Validators.max(20),
    ]),
    cncTimeMultiplier: new FormControl<number | null>(null, [
      Validators.min(0.1),
      Validators.max(20),
    ]),
    finishingTimeMultiplier: new FormControl<number | null>(null, [
      Validators.min(0.1),
      Validators.max(20),
    ]),
    // DFM Checks
    countHoles: new FormControl<boolean>(true),
    unmachinableHoles: new FormControl<boolean>(true),
    drillHeadRadius: new FormControl<number>(50.0),
    drillBitRatioBlind: new FormControl<number>(3.0),
    drillBitRatioThrough: new FormControl<number>(5.0),
    thinWalls: new FormControl<boolean>(true),
    minThickness: new FormControl<number>(0.15),
    maxThickness: new FormControl<number>(4.0),
    narrowGaps: new FormControl<boolean>(true),
    angleTolerance: new FormControl<number>(15),
    internalVolume: new FormControl<boolean>(true),
    blockingUp: new FormControl<boolean>(true),
    description: new FormControl(''),
  });

  get sheetSizesArray(): FormArray<
    FormGroup<{
      x: FormControl<number | null>;
      y: FormControl<number | null>;
      z: FormControl<number | null>;
    }>
  > {
    return this.materialFormGroup.controls.sheetSizes;
  }

  get sheetSizeRows(): FormGroup<{
    x: FormControl<number | null>;
    y: FormControl<number | null>;
    z: FormControl<number | null>;
  }>[] {
    return this.sheetSizesArray.controls;
  }

  addSheetSizeRow(
    x: number | null = null,
    y: number | null = null,
    z: number | null = null,
  ): void {
    this.sheetSizesArray.push(
      new FormGroup({
        x: new FormControl<number | null>(x, [
          Validators.required,
          Validators.min(MIN_MATERIAL_SHEET_DIMENSION_MM),
        ]),
        y: new FormControl<number | null>(y, [
          Validators.required,
          Validators.min(MIN_MATERIAL_SHEET_DIMENSION_MM),
        ]),
        z: new FormControl<number | null>(z, [
          Validators.required,
          Validators.min(MIN_MATERIAL_SHEET_DIMENSION_MM),
        ]),
      }),
    );
  }

  removeSheetSizeRow(index: number): void {
    this.sheetSizesArray.removeAt(index);
    this.materialFormGroup.markAsDirty();
  }

  priceTypeOptions = [
    { label: 'Area (m²)', value: 'area' },
    { label: 'Volume (m³)', value: 'volume' },
    { label: 'Volume (L)', value: 'volume_l' },
  ];

  getPriceTypeLabel(value: string): string {
    return this.priceTypeOptions.find((o) => o.value === value)?.label ?? value;
  }

  get isSuperuser(): boolean {
    return this.currentUser?.role === 'superuser';
  }

  get materialPriceSuperscript(): string {
    const priceType = this.materialFormGroup.controls.priceType.value;
    if (priceType === 'volume') return 'm³';
    if (priceType === 'volume_l') return 'L';
    return 'm²';
  }

  ngOnInit() {
    this.userService.loadCurrentUser().then((user) => {
      this.currentUser = user ?? undefined;
    });

    const blockingUp = this.materialFormGroup.get('blockingUp');

    if (blockingUp) {
      const subscription = blockingUp.valueChanges
        .pipe(startWith(blockingUp.value))
        .subscribe(() => this.updateSheetSizesValidator());

      this.updateSheetSizesValidator();
      this.subscriptions.add(subscription);
    }
  }

  private updateSheetSizesValidator(): void {
    const blockingUp = this.materialFormGroup.get('blockingUp');
    const sheetSizesArray = this.sheetSizesArray;

    if (!blockingUp) {
      return;
    }

    const isBlockingUp = blockingUp.value === true;

    if (isBlockingUp) {
      sheetSizesArray.enable({ emitEvent: false });
      sheetSizesArray.setValidators([atLeastOneFormArrayItem()]);

      if (sheetSizesArray.length === 0) {
        this.addSheetSizeRow();
      }
    } else {
      sheetSizesArray.clearValidators();
      sheetSizesArray.disable({ emitEvent: false });
    }

    sheetSizesArray.updateValueAndValidity({ emitEvent: false });
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  isInvalid(controlName: string): boolean {
    const control = this.materialFormGroup.get(controlName);
    return !!(
      control?.invalid &&
      (control.touched || this.materialFormGroup.dirty)
    );
  }

  isRequired(controlName: string): boolean {
    const control = this.materialFormGroup.get(controlName);
    return !!(
      (control?.errors?.['required'] || control?.errors?.['min']) &&
      (control.touched || this.materialFormGroup.dirty)
    );
  }

  openDrawer(material: Material | null = null) {
    if (material) {
      // Clear any pending attachment changes from previous edits
      this.pendingFiles = [];
      this.deletedAttachmentIds = [];

      // Fetch fresh material data to ensure attachments are loaded
      this.materialsService
        .getMaterial$(material.id)
        .subscribe((freshMaterial) => {
          // Populate form with material data
          this.sheetSizesArray.clear();
          (freshMaterial.sheet_sizes || []).forEach((s) => {
            this.addSheetSizeRow(s.x, s.y, s.z);
          });
          this.materialFormGroup.patchValue(
            {
              name: freshMaterial.name,
              price: freshMaterial.material_price,
              priceType: freshMaterial.material_price_type,
              densityGramsPerm3:
                freshMaterial.density_kilogram_per_m3 != null
                  ? Number(freshMaterial.density_kilogram_per_m3)
                  : 0,
              surfaceHardness: freshMaterial.surface_hardness ?? null,
              curedThicknessMm:
                freshMaterial.cured_thickness_mm != null
                  ? Number(freshMaterial.cured_thickness_mm)
                  : null,
              compositeTimeMultiplier:
                freshMaterial.composite_time_multiplier != null
                  ? Number(freshMaterial.composite_time_multiplier)
                  : 1.0,
              cncTimeMultiplier:
                freshMaterial.cnc_time_multiplier != null
                  ? Number(freshMaterial.cnc_time_multiplier)
                  : null,
              finishingTimeMultiplier:
                freshMaterial.finishing_time_multiplier != null
                  ? Number(freshMaterial.finishing_time_multiplier)
                  : null,
              // DFM Checks
              countHoles: freshMaterial.count_holes,
              unmachinableHoles: freshMaterial.unmachinable_holes,
              drillHeadRadius: Number(freshMaterial.drill_head_radius_mm),
              drillBitRatioBlind: Number(
                freshMaterial.drill_bit_length_to_diameter_ratio_blind,
              ),
              drillBitRatioThrough: Number(
                freshMaterial.drill_bit_length_to_diameter_ratio_through,
              ),
              thinWalls: freshMaterial.thin_walls,
              minThickness: Number(freshMaterial.min_thickness_mm),
              maxThickness: Number(freshMaterial.max_thickness_mm),
              narrowGaps: freshMaterial.narrow_gaps,
              angleTolerance: Number(freshMaterial.angle_tolerance_degrees),
              internalVolume: freshMaterial.internal_volume,
              blockingUp: freshMaterial.blocking_up,
              description: freshMaterial.description,
            },
            { emitEvent: false },
          );
          this.updateSheetSizesValidator();
          this.materialFormGroup.markAsPristine();
          this.materialInDraw = freshMaterial;
          this.isDrawerVisible = true;
        });
    } else {
      this.sheetSizesArray.clear();
      // Reset form for new material
      this.materialFormGroup.reset({
        name: '',
        price: '0',
        priceType: 'area',
        densityGramsPerm3: 0,
        surfaceHardness: null,
        curedThicknessMm: null,
        compositeTimeMultiplier: 1.0,
        cncTimeMultiplier: null,
        finishingTimeMultiplier: null,
        countHoles: true,
        unmachinableHoles: true,
        drillHeadRadius: 50.0,
        drillBitRatioBlind: 3.0,
        drillBitRatioThrough: 5.0,
        thinWalls: true,
        minThickness: 0.15,
        maxThickness: 4.0,
        narrowGaps: true,
        angleTolerance: 15,
        internalVolume: false,
        blockingUp: false,
        description: '',
      });
      this.updateSheetSizesValidator();
      this.materialInDraw = null;
      this.pendingFiles = [];
      this.deletedAttachmentIds = [];
      this.isDrawerVisible = true;
    }
  }

  closeDrawer() {
    this.isDrawerVisible = false;
    this.materialFormGroup.reset();
    this.pendingFiles = [];
    this.deletedAttachmentIds = [];
    this.saveError = null;
  }

  async saveMaterial() {
    if (this.materialFormGroup.valid) {
      // Clear previous errors
      this.saveError = null;

      const formValue = this.materialFormGroup.getRawValue();
      const sheetSizes = (formValue.sheetSizes || [])
        .map((row: { x: number | null; y: number | null; z: number | null }) =>
          row.x != null && row.y != null && row.z != null
            ? { x: row.x, y: row.y, z: row.z }
            : null,
        )
        .filter(
          (
            s: { x: number; y: number; z: number } | null,
          ): s is { x: number; y: number; z: number } => s !== null,
        );

      const materialData: Partial<Material> = {
        name: formValue.name || '',
        material_price: formValue.price || '0',
        material_price_type: formValue.priceType || 'area',
        sheet_sizes: sheetSizes,
        density_kilogram_per_m3:
          formValue.densityGramsPerm3 != null
            ? String(formValue.densityGramsPerm3)
            : '0',
        surface_hardness: formValue.surfaceHardness ?? null,
        cured_thickness_mm:
          formValue.curedThicknessMm != null
            ? String(formValue.curedThicknessMm)
            : null,
        // DFM Checks
        count_holes: formValue.countHoles ?? true,
        unmachinable_holes: formValue.unmachinableHoles ?? true,
        drill_head_radius_mm: formValue.drillHeadRadius?.toString() || '50.0',
        drill_bit_length_to_diameter_ratio_blind:
          formValue.drillBitRatioBlind?.toString() || '3.0',
        drill_bit_length_to_diameter_ratio_through:
          formValue.drillBitRatioThrough?.toString() || '5.0',
        thin_walls: formValue.thinWalls ?? true,
        min_thickness_mm: formValue.minThickness?.toString() || '0.15',
        max_thickness_mm: formValue.maxThickness?.toString() || '4.0',
        narrow_gaps: formValue.narrowGaps ?? true,
        angle_tolerance_degrees: formValue.angleTolerance?.toString() || '15',
        internal_volume: formValue.internalVolume ?? false,
        blocking_up: formValue.blockingUp ?? false,
        description: formValue.description ?? '',
      };

      if (this.isSuperuser) {
        materialData.cnc_time_multiplier =
          formValue.cncTimeMultiplier != null
            ? formValue.cncTimeMultiplier.toString()
            : null;
        materialData.composite_time_multiplier =
          formValue.compositeTimeMultiplier?.toString() ?? '1.0';
        materialData.finishing_time_multiplier =
          formValue.finishingTimeMultiplier != null
            ? formValue.finishingTimeMultiplier.toString()
            : null;
      }

      try {
        let savedMaterial: Material;
        if (this.materialInDraw) {
          savedMaterial = await this.materialsService.putMaterial(
            this.materialInDraw.id,
            materialData,
          );
        } else {
          savedMaterial =
            await this.materialsService.createMaterial(materialData);
        }

        // Upload pending files with error handling
        for (const file of this.pendingFiles) {
          try {
            await this.materialsService.uploadAttachment(
              savedMaterial.id,
              file,
              false,
            );
          } catch (uploadErr) {
            this.handleUploadError(uploadErr, file.name);
            return; // Stop on first error
          }
        }

        // Delete marked attachments
        for (const attachmentId of this.deletedAttachmentIds) {
          await this.materialsService.deleteAttachment(attachmentId, false);
        }

        // Refresh materials list
        await this.materialsService.loadMaterials();

        this.materialInDraw = null;
        this.closeDrawer();
      } catch (error) {
        this.handleSaveError(error);
      }
    }
  }

  openConfirm(
    event: Event,
    material: Material,
    action: 'archive' | 'unarchive',
  ): void {
    const isArchive = action === 'archive';

    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `Are you sure that you want to ${action} ${material.name}?`,
      header: `${isArchive ? 'Archive' : 'Unarchive'} ${material.name}`,
      closable: true,
      closeOnEscape: true,
      icon: isArchive ? 'pi pi-exclamation-triangle' : 'pi pi-refresh',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: isArchive ? 'Archive' : 'Unarchive',
      },
      accept: () => {
        void this.handleMaterialAction(action, material);
      },
    });
  }

  private async handleMaterialAction(
    action: 'archive' | 'unarchive',
    material: Material,
  ): Promise<void> {
    try {
      const fn =
        action === 'archive'
          ? this.materialsService.archiveMaterial
          : this.materialsService.unarchiveMaterial;

      await fn.call(this.materialsService, material.id);
    } catch (err: unknown) {
      this.saveError = parseApiError(err);
    }
  }

  onAttachmentSelect(event: FileSelectEvent) {
    if (event.files.length > 0) {
      // Convert to array for proper handling
      const filesArray = Array.from(event.files);

      // Filter out files that exceed the maximum size (50 MB)
      const validFiles = filesArray.filter(
        (file) => file.size <= this.MAX_FILE_SIZE,
      );

      // Add only valid files to pending array (deferred upload)
      this.pendingFiles.push(...validFiles);
      // Mark form as dirty so Save button becomes enabled
      if (validFiles.length > 0) {
        this.materialFormGroup.markAsDirty();
      }
    }
  }

  removeAttachment(attachment: MaterialAttachment) {
    // Mark for deletion (deferred)
    this.deletedAttachmentIds.push(attachment.id);
    // Mark form as dirty so Save button becomes enabled
    this.materialFormGroup.markAsDirty();
  }

  removePendingFile(index: number) {
    this.pendingFiles.splice(index, 1);
    this.materialFormGroup.markAsDirty();
  }

  isAttachmentDeleted(attachmentId: number): boolean {
    return this.deletedAttachmentIds.includes(attachmentId);
  }

  async downloadAttachment(attachment: MaterialAttachment) {
    if (attachment.file_url) {
      await this.downloadService.downloadFile(attachment.file_url, {
        defaultFilename: attachment.original_filename,
        mimeType: 'application/octet-stream',
      });
    }
  }

  openAttachmentsModal(material: Material) {
    this.modalMaterial = material;
    this.isAttachmentsModalVisible = true;
  }

  closeAttachmentsModal() {
    this.isAttachmentsModalVisible = false;
    this.modalMaterial = null;
  }

  getAttachmentCount(material: Material): number {
    return material.attachments?.length || 0;
  }

  /** Price staleness threshold in days */
  private readonly PRICE_STALE_DAYS = 14;

  /**
   * Check if a material's price is stale (not updated in over 2 weeks).
   */
  isPriceStale(material: Material): boolean {
    const updatedDate = new Date(material.price_updated_at);
    const now = new Date();
    const diffTime = now.getTime() - updatedDate.getTime();
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    return diffDays > this.PRICE_STALE_DAYS;
  }

  /**
   * Format the price update date for display.
   */
  formatPriceUpdateDate(date: string): string {
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  /**
   * Handle file upload errors (matches pattern from quote-item-edit.component)
   */
  private handleUploadError(error: unknown, filename: string): void {
    if (isPermissionError(error)) return;
    this.saveError = parseApiError(
      error,
      `An error occurred while uploading ${filename}.`,
    );
    console.error('Upload error:', error);
    throw error;
  }

  /**
   * Handle general save errors
   */
  private handleSaveError(error: unknown): void {
    if (isPermissionError(error)) return;
    this.saveError = parseApiError(
      error,
      'An error occurred while saving the material.',
    );
    console.error('Save error:', error);
    throw error;
  }
}

export function atLeastOneFormArrayItem(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!(control instanceof FormArray)) {
      return null;
    }

    return control.length > 0 ? null : { atLeastOneSheetSize: true };
  };
}
